import React, { useState } from 'react';
import {
    Card,
    Tabs,
    Button,
    Space,
    Typography,
    Input,
    DatePicker,
    Select,
    Form
} from 'antd';
import {
    ReloadOutlined,
    FilterOutlined,
    SearchOutlined
} from '@ant-design/icons';
import RequestList from '../components/requests/RequestList';
import { useRequests } from "@/modules/action-flow/runtime/hooks/useRequests";
import { ActionFlowRequestSearch } from "@/shared/api/model";

const { Title } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

export const RequestHistoryView: React.FC = () => {
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState('approved');
    const [searchParams, setSearchParams] = useState<Omit<ActionFlowRequestSearch, 'status'>>({
        numberOfRows: 40,
        sortBy: 'scopeDate',
        orderBy: 'desc'
    });

    const { requests, isLoading, error, refetch } = useRequests({
        ...searchParams,
        // @ts-ignore
        status: activeTab.toUpperCase()
    });

    const handleSearch = (values: any) => {
        const newParams = {
            ...searchParams,
            ...values,
            dateRange: values.dateRange
                ? [values.dateRange[0].format('YYYY-MM-DD'), values.dateRange[1].format('YYYY-MM-DD')]
                : undefined
        };
        setSearchParams(newParams);
    };

    const handleTabChange = (key: string) => {
        setActiveTab(key);
    };

    const handleReset = () => {
        form.resetFields();
        setSearchParams({
            numberOfRows: 40,
            sortBy: 'scopeDate',
            orderBy: 'desc'
        });
    };

    const renderSearchForm = () => (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleSearch}
            className="mb-4"
        >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Form.Item name="q" label="Search">
                    <Input
                        prefix={<SearchOutlined />}
                        placeholder="Search records..."
                        allowClear
                    />
                </Form.Item>

                <Form.Item name="domain" label="Domain">
                    <Select
                        placeholder="Select domain"
                        allowClear
                        options={[
                            { label: 'Loans', value: 'LOANS' },
                            { label: 'Accounts', value: 'ACCOUNTS' },
                            { label: 'Customers', value: 'CUSTOMERS' }
                        ]}
                    />
                </Form.Item>

                <Form.Item name="dateRange" label="Date Range">
                    <RangePicker style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item label=" " className="flex justify-end">
                    <Space>
                        <Button onClick={handleReset}>
                            Reset
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<FilterOutlined />}
                        >
                            Filter
                        </Button>
                    </Space>
                </Form.Item>
            </div>
        </Form>
    );

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <Title level={2}>Request History</Title>
                <Button
                    icon={<ReloadOutlined />}
                    onClick={() => refetch()}
                    loading={isLoading}
                >
                    Refresh
                </Button>
            </div>

            <Card>
                {renderSearchForm()}

                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    className="mb-4"
                >
                    <TabPane tab="Pending" key="pending" />
                    <TabPane tab="Approved" key="approved" />
                    <TabPane tab="Rejected" key="rejected" />
                    <TabPane tab="Returned" key="returned" />
                    <TabPane tab="Completed" key="completed" />
                </Tabs>

                <RequestList
                    requests={requests}
                    isLoading={isLoading}
                    error={error instanceof Error ? error : null}
                    showResendAction={activeTab === 'completed' || activeTab === 'approved'}
                    // @ts-ignore
                    hideSubmissionStep={true}
                    // @ts-ignore
                    searchParams={undefined}
                    // @ts-ignore
                    onParamsChange={function (params: ActionFlowRequestSearch): void {
                        throw new Error('Function not implemented.');
                    }}
                />
            </Card>

            {/* Help Section */}
            <Card title="Help & Information" className="mt-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <Title level={4}>Request Status</Title>
                        <ul className="list-disc pl-4">
                            <li>Approved: Successfully approved requests</li>
                            <li>Rejected: Declined requests</li>
                            <li>Returned: Requests sent back for revision</li>
                            <li>Completed: Fully processed requests</li>
                        </ul>
                    </div>
                    <div>
                        <Title level={4}>Available Actions</Title>
                        <ul className="list-disc pl-4">
                            <li>View: See full request details and history</li>
                            <li>Resend Notification: Reissue notification for completed/approved requests</li>
                        </ul>
                    </div>
                    <div>
                        <Title level={4}>Search Tips</Title>
                        <ul className="list-disc pl-4">
                            <li>Use the search box to find specific records</li>
                            <li>Filter by date range to narrow results</li>
                            <li>Select domain to focus on specific areas</li>
                            <li>Use Reset to clear all filters</li>
                        </ul>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default RequestHistoryView;
